import type { StayingCondition, TravelSnapshot } from "../../../../domain/travel";

export type {
  Journey,
  StayingCondition,
  TravelLeg,
  TravelQuestionnaireAnswer,
  TravelRecord,
  TravelSnapshot,
} from "../../../../domain/travel";

export const EMPTY_TRAVEL: TravelSnapshot = {
  journey: {
    legs: [],
    staying_conditions: null,
  },
  conditions: [],
  viewedQuestionnaireSectionIds: [],
};

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isStayingCondition(value: unknown): value is StayingCondition {
  return value === "comfortable" || value === "intermediate" || value === "adventurous";
}

export function isTravelSnapshot(value: unknown): value is TravelSnapshot {
  if (!isObject(value) || !isObject(value.journey) || !Array.isArray(value.journey.legs)) return false;
  if (value.journey.staying_conditions !== null && !isStayingCondition(value.journey.staying_conditions)) return false;

  const hasValidLegs = value.journey.legs.every(
    (leg) =>
      isObject(leg) &&
      (leg.countrycode === null || typeof leg.countrycode === "string") &&
      (leg.start_date === null || typeof leg.start_date === "string") &&
      (leg.end_date === null || typeof leg.end_date === "string") &&
      (leg.staying_conditions === null || isStayingCondition(leg.staying_conditions)),
  );
  if (
    !hasValidLegs ||
    !Array.isArray(value.conditions) ||
    !Array.isArray(value.viewedQuestionnaireSectionIds) ||
    !value.viewedQuestionnaireSectionIds.every((sectionId) => typeof sectionId === "string" && sectionId.length > 0)
  ) {
    return false;
  }

  return value.conditions.every(
    (condition) =>
      isObject(condition) &&
      typeof condition.id === "string" &&
      (condition.value === null ||
        typeof condition.value === "boolean" ||
        typeof condition.value === "number" ||
        typeof condition.value === "string"),
  );
}

/** Encodes the complete travel snapshot expected by Mentor's `travel-data` parameter. */
export function encodeTravelDiagnosticData(travel: TravelSnapshot): string {
  const bytes = new TextEncoder().encode(JSON.stringify({ version: 1, travel }));
  const binary = Array.from(bytes, (byte) => String.fromCharCode(byte)).join("");

  return btoa(binary).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function getTravelDestinationNames(travel: TravelSnapshot, t: (key: string) => string): string {
  const countryCodes = travel.journey.legs
    .map(({ countrycode }) => countrycode)
    .filter((countrycode): countrycode is string => countrycode !== null);

  return countryCodes.length > 0
    ? countryCodes.map((countryCode) => t(`countries.${countryCode}`)).join(", ")
    : t("travel.no_destination");
}

export function getTravelPeriod(travel: TravelSnapshot): { startDate: string; endDate: string } | undefined {
  const datedLegs = travel.journey.legs.filter(
    (leg): leg is typeof leg & { start_date: string; end_date: string } =>
      leg.start_date !== null && leg.end_date !== null,
  );
  if (datedLegs.length === 0) return undefined;

  return {
    startDate: datedLegs.reduce(
      (earliest, leg) => (leg.start_date < earliest ? leg.start_date : earliest),
      datedLegs[0].start_date,
    ),
    endDate: datedLegs.reduce((latest, leg) => (leg.end_date > latest ? leg.end_date : latest), datedLegs[0].end_date),
  };
}

export function isTravelPast(travel: TravelSnapshot, today: Date = new Date()): boolean {
  const period = getTravelPeriod(travel);
  if (!period) return false;

  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return period.endDate < `${year}-${month}-${day}`;
}
